import React, { useState } from 'react';
import { Check, Copy, Link2, RotateCcw } from 'lucide-react';

const PRESETS = [
  { hex: '#0d9488', label: 'Teal (default)' },
  { hex: '#1d4ed8', label: 'Blue' },
  { hex: '#b91c1c', label: 'Red' },
  { hex: '#c2410c', label: 'Orange' },
  { hex: '#15803d', label: 'Green' },
  { hex: '#6d28d9', label: 'Violet' },
  { hex: '#0f172a', label: 'Charcoal' },
];

const HEX = /^#?[0-9A-Fa-f]{6}$/;

export const BrandColorPicker = ({ value, onChange }) => {
  const current = value || '#0d9488';
  const [text, setText] = useState(current);

  const commit = (hex) => {
    const clean = `#${hex.replace('#', '').toLowerCase()}`;
    setText(clean);
    onChange(clean);
  };

  return (
    <div className="space-y-3" data-testid="brand-color-picker">
      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.hex}
            type="button"
            title={p.label}
            onClick={() => commit(p.hex)}
            className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 ${
              current.toLowerCase() === p.hex
                ? 'border-slate-900 dark:border-white'
                : 'border-white dark:border-white/40 shadow-sm'
            }`}
            style={{ backgroundColor: p.hex }}
            data-testid={`brand-color-${p.hex.slice(1)}`}
          >
            {current.toLowerCase() === p.hex && <Check size={14} className="mx-auto text-white" />}
          </button>
        ))}
        <label className="inline-flex items-center gap-2 ml-1 text-xs text-slate-500">
          <input
            type="color"
            value={current}
            onChange={(e) => commit(e.target.value)}
            className="h-8 w-10 rounded border border-slate-300 bg-white p-0.5 cursor-pointer"
            data-testid="brand-color-input"
          />
          custom
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => (HEX.test(text) ? commit(text) : setText(current))}
          placeholder="#0d9488"
          className="w-32 font-mono text-sm px-2.5 py-1.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
          data-testid="brand-color-hex"
        />
        {value && (
          <button
            type="button"
            onClick={() => { setText('#0d9488'); onChange(''); }}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50"
            data-testid="brand-color-reset"
          >
            <RotateCcw size={12} /> Reset to default
          </button>
        )}
      </div>
      <p className="text-xs text-slate-500">
        Used for headings and highlights across your documents and the staff portal.
        Save to apply.
      </p>
    </div>
  );
};

export const StaffInviteLinks = ({ slug }) => {
  const [copied, setCopied] = useState('');
  if (!slug) return null;

  // One link only: sign-in already sends each person to the right place for
  // their role, so a separate portal URL would just be a broken promise for
  // anyone not signed in yet.
  const origin = window.location.origin;
  const links = [
    { key: 'staff', label: 'Staff sign-in', url: `${origin}/admin/login?w=${slug}`,
      hint: 'Share with new team members — opens a sign-in page carrying your logo and colour' },
  ];

  const copy = async (key, url) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard needs a secure context and permission; a selectable field is
      // the fallback so the link is never trapped in the page.
      window.prompt('Copy this link', url);
    }
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <div className="space-y-3" data-testid="staff-invite-links">
      {links.map((l) => (
        <div key={l.key} className="space-y-1">
          <div className="flex items-center gap-2">
            <Link2 size={13} className="text-slate-400 shrink-0" />
            <input
              readOnly
              value={l.url}
              onFocus={(e) => e.target.select()}
              className="flex-1 min-w-0 font-mono text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 bg-slate-50 text-slate-600"
              data-testid={`invite-link-${l.key}`}
            />
            <button
              type="button"
              onClick={() => copy(l.key, l.url)}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 shrink-0"
              data-testid={`invite-copy-${l.key}`}
            >
              {copied === l.key ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
              {copied === l.key ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="text-[10px] text-slate-400 pl-5">{l.label} — {l.hint}</div>
        </div>
      ))}
    </div>
  );
};
